import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Avatar } from '../components/Avatar';
import { AvatarCropper } from '../components/AvatarCropper';
import { useSession } from '../lib/session';
import { supabase } from '../lib/supabase';
import { withTimeout } from '../lib/timeout';
import { updateOwnProfile, uploadProfileImage, type ProfileEditPatch } from '../lib/queries';
import { checkHandleAvailable, markOnboardingComplete } from '../lib/onboarding-queries';
import { getPlatform } from '../lib/socials';
import { countryName } from '../lib/countries';
import { CountrySelect } from '../components/CountrySelect';
import { FlagIcon } from '../components/FlagIcon';
import './Onboarding.css';

/**
 * Multi-step onboarding wizard.
 *
 * First-run gate before the feed. 4 steps:
 *   1. Handle + full name (both required). Handle leads — it's the public name.
 *   2. Photo (with a circular cropper), bio, location (country required + an
 *      optional city/area), and Instagram. All but the country are optional.
 *   3. Password (required — everyone sets one).
 *   4. Summary + final commit.
 *
 * Every step saves progress via updateOwnProfile so a refresh mid-wizard
 * won't lose fields the user already filled. The form starts blank — we never
 * pre-fill from the profile, so an email-derived name can't leak in.
 */

const HANDLE_PATTERN = /^[a-z0-9_-]{3,30}$/;
const HANDLE_CHAR = /[^a-z0-9_-]/g;
const TOTAL_STEPS = 4;
const STEP_PASSWORD = 2;

const IG = getPlatform('instagram')!;

type HandleStatus =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'ok' }
  | { kind: 'bad'; reason: string };

export default function Onboarding() {
  const { profile, loading } = useSession();
  // Staff preview (?preview=1): walk the wizard read-only — no profile writes,
  // no password change, no commit — so testing never mutates the staffer.
  const preview = new URLSearchParams(useLocation().search).get('preview') === '1';

  const [step, setStep] = useState(0);

  // Form always starts blank — we deliberately do NOT pre-fill from the
  // profile. A display_name derived from the invite email at signup must not
  // leak into the wizard; the member types everything themselves. Each step
  // still saves what's entered (saveProgress), so the final commit is whole.
  const [displayName, setDisplayName] = useState('');
  const [handle, setHandle] = useState('');
  const [bio, setBio] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const [ig, setIg] = useState('');
  const [country, setCountry] = useState('');
  const [town, setTown] = useState('');

  // Password step. Required now — everyone sets one.
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSaved, setPwSaved] = useState(false);

  const [handleStatus, setHandleStatus] = useState<HandleStatus>({ kind: 'idle' });
  const [savingStep, setSavingStep] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);
  // The raw file the member picked, held while the cropper is open.
  const [cropFile, setCropFile] = useState<File | null>(null);

  const avatarInput = useRef<HTMLInputElement>(null);

  // Debounced handle availability check
  useEffect(() => {
    const trimmed = handle.trim().toLowerCase();
    if (!trimmed) {
      setHandleStatus({ kind: 'idle' });
      return;
    }
    if (trimmed.length < 3) {
      setHandleStatus({ kind: 'bad', reason: 'At least 3 characters.' });
      return;
    }
    if (trimmed.length > 30) {
      setHandleStatus({ kind: 'bad', reason: 'Up to 30 characters.' });
      return;
    }
    if (!HANDLE_PATTERN.test(trimmed)) {
      setHandleStatus({ kind: 'bad', reason: 'Letters, numbers, dashes, underscores only.' });
      return;
    }
    if (profile?.handle && profile.handle.toLowerCase() === trimmed) {
      setHandleStatus({ kind: 'ok' });
      return;
    }
    setHandleStatus({ kind: 'checking' });
    const ctrl = new AbortController();
    const t = window.setTimeout(async () => {
      try {
        const ok = await checkHandleAvailable(trimmed, ctrl.signal);
        if (ctrl.signal.aborted) return;
        setHandleStatus(
          ok ? { kind: 'ok' } : { kind: 'bad', reason: "That one's taken. Try another." },
        );
      } catch (err: any) {
        if (ctrl.signal.aborted || err?.name === 'AbortError') return;
        setHandleStatus({ kind: 'bad', reason: err?.message ?? 'Could not check availability.' });
      }
    }, 400);
    return () => { window.clearTimeout(t); ctrl.abort(); };
  }, [handle, profile?.handle]);

  const displayNameValid = displayName.trim().length >= 2;
  const handleValid = handleStatus.kind === 'ok';
  const countryValid = !!country;
  const passwordValid = pw1.length >= 8 && pw1 === pw2;

  const canAdvanceStep0 = handleValid && displayNameValid;
  const canAdvanceStep1 = countryValid; // photo / bio / town / Instagram optional
  const canFinish = preview || (canAdvanceStep0 && countryValid && (pwSaved || passwordValid));

  // Persist helper — writes what's valid now, ignores fields not yet filled.
  async function saveProgress(extra?: ProfileEditPatch) {
    if (!profile || preview) return;
    const patch: ProfileEditPatch = { ...(extra ?? {}) };
    if (displayNameValid) patch.display_name = displayName.trim();
    if (handleValid) patch.handle = handle.trim().toLowerCase();
    if (bio.trim()) patch.bio = bio.trim();
    if (avatarUrl) patch.avatar_url = avatarUrl;
    if (ig.trim()) patch.instagram_handle = IG.sanitize(ig);
    if (country) patch.country = country;
    if (town.trim()) patch.location = town.trim();
    try {
      await updateOwnProfile(profile.id, patch);
    } catch (err: any) {
      console.warn('[onboarding] save progress failed', err);
    }
  }

  // Upload the cropped avatar and persist it immediately.
  async function uploadAvatar(file: File) {
    if (!profile) return;
    // Preview mode: show the crop locally, don't upload or persist.
    if (preview) { setAvatarUrl(URL.createObjectURL(file)); return; }
    setUploadMsg('Uploading…');
    try {
      const url = await uploadProfileImage({ userId: profile.id, kind: 'avatar', file });
      setAvatarUrl(url);
      setUploadMsg(null);
      await updateOwnProfile(profile.id, { avatar_url: url });
    } catch (err: any) {
      console.error('[onboarding] upload failed', err);
      setUploadMsg(err?.message ?? 'Upload failed.');
    }
  }

  // Set the password. Required: returns false (and holds the step) if invalid.
  async function commitPassword(): Promise<boolean> {
    setPwError(null);
    if (pwSaved && pw1.length === 0) return true; // already saved, nothing new typed
    // Validate FIRST (length + match) — these run in preview too so the flow
    // behaves identically; preview just skips the actual network write below.
    if (pw1.length < 8) { setPwError('At least 8 characters.'); return false; }
    if (pw1 !== pw2) { setPwError("Passwords don't match."); return false; }
    if (preview) { setPwSaved(true); return true; } // read-only: don't change the staffer's password
    try {
      const { error: upErr } = await withTimeout(
        supabase.auth.updateUser({ password: pw1 }),
        15000,
        'Password update',
      );
      if (upErr) throw upErr;
      setPwSaved(true);
      return true;
    } catch (err: any) {
      setPwError(err?.message ?? 'Could not set the password. Try again.');
      return false;
    }
  }

  async function next() {
    if (!profile) return;
    setError(null);
    setSavingStep(true);
    try {
      if (step === STEP_PASSWORD && !(await commitPassword())) return;
      await saveProgress();
      setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
    } finally {
      setSavingStep(false);
    }
  }

  function back() {
    setError(null);
    setStep((s) => Math.max(s - 1, 0));
  }

  async function finish() {
    if (!profile || !canFinish) return;
    // Preview mode: nothing to commit — just leave the wizard.
    if (preview) { window.location.assign('/'); return; }
    setError(null);
    setFinishing(true);
    try {
      const patch: ProfileEditPatch = {
        display_name: displayName.trim(),
        handle: handle.trim().toLowerCase(),
        bio: bio.trim(),
        avatar_url: avatarUrl,
        instagram_handle: ig.trim() ? IG.sanitize(ig) : null,
        country,
        location: town.trim() || null,
      };
      await markOnboardingComplete(profile.id, patch);
      window.location.assign('/');
    } catch (err: any) {
      console.error('[onboarding] finish failed', err);
      setError(err?.message ?? 'Could not save. Try again.');
      setFinishing(false);
    }
  }

  const eyebrow = useMemo(() => {
    switch (step) {
      case 0: return 'Before we let you in';
      case 1: return 'A little about you';
      case 2: return 'Your way back in';
      case 3: return 'Ready when you are';
      default: return '';
    }
  }, [step]);

  if (loading) {
    return (
      <div
        role="status"
        aria-label="Loading"
        style={{ minHeight: '60dvh', display: 'grid', placeItems: 'center', color: 'var(--text-soft)' }}
      >
        …
      </div>
    );
  }
  if (!profile) return null;

  return (
    <div className="onb">
      <div className="onb__inner">
        <div className="onb__progress">
          <span className="onb__step-label">Step {step + 1} of {TOTAL_STEPS}</span>
          <div className="onb__bars" aria-hidden="true">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <span key={i} className={`onb__bar${i <= step ? ' onb__bar--on' : ''}`} />
            ))}
          </div>
        </div>

        <div className="onb__card">
          <div>
            <p className="onb__eyebrow">{eyebrow}</p>
          </div>

          {step === 0 && (
            <StepIdentity
              displayName={displayName}
              setDisplayName={setDisplayName}
              handle={handle}
              setHandle={setHandle}
              handleStatus={handleStatus}
            />
          )}

          {step === 1 && (
            <StepProfile
              avatarUrl={avatarUrl}
              setAvatarUrl={setAvatarUrl}
              bio={bio}
              setBio={setBio}
              displayName={displayName}
              onPickAvatar={() => avatarInput.current?.click()}
              uploadMsg={uploadMsg}
              country={country}
              setCountry={setCountry}
              town={town}
              setTown={setTown}
              ig={ig}
              setIg={setIg}
            />
          )}

          {step === 2 && (
            <StepPassword
              pw1={pw1}
              setPw1={(v) => { setPw1(v); setPwError(null); }}
              pw2={pw2}
              setPw2={(v) => { setPw2(v); setPwError(null); }}
              pwError={pwError}
            />
          )}

          {step === 3 && (
            <StepSummary
              displayName={displayName}
              handle={handle}
              bio={bio}
              avatarUrl={avatarUrl}
              ig={ig}
              country={country}
              town={town}
            />
          )}

          {error && <p className="onb__error">{error}</p>}

          <div className="onb__footer">
            <div className="onb__footer-left">
              {step > 0 && (
                <button type="button" className="btn btn--ghost btn--md" onClick={back} disabled={savingStep || finishing}>
                  Back
                </button>
              )}
            </div>
            <div className="onb__footer-right">
              {step < TOTAL_STEPS - 1 && (
                <button
                  type="button"
                  className="btn btn--primary btn--md"
                  onClick={next}
                  disabled={
                    savingStep ||
                    (step === 0 && !canAdvanceStep0) ||
                    (step === 1 && !canAdvanceStep1) ||
                    (step === STEP_PASSWORD && !passwordValid)
                  }
                >
                  {savingStep ? 'Saving…' : 'Next'}
                </button>
              )}
              {step === TOTAL_STEPS - 1 && (
                <button
                  type="button"
                  className="btn btn--primary btn--md onb__cta--final"
                  onClick={finish}
                  disabled={!canFinish || finishing}
                >
                  {finishing ? 'Opening the door…' : 'Enter the Club'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Accent-bar pointer — only on the first page. */}
        {step === 0 && (
          <p className="onb__accent-note">
            <span className="onb__accent-arrow" aria-hidden="true">↓</span>
            <span>
              Customize your feed using the colour bar below. Swipe sideways on a
              phone to see all colours.
            </span>
          </p>
        )}

        {/* Hidden avatar file input — opens the cropper rather than uploading raw. */}
        <input
          ref={avatarInput}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            e.target.value = ''; // allow re-picking the same file
            if (f) setCropFile(f);
          }}
        />
      </div>

      {cropFile && (
        <AvatarCropper
          file={cropFile}
          onCancel={() => setCropFile(null)}
          onConfirm={async (cropped) => {
            setCropFile(null);
            await uploadAvatar(cropped);
          }}
        />
      )}
    </div>
  );
}

// ---------- Step panels ----------------------------------------------------

function StepIdentity(props: {
  displayName: string;
  setDisplayName: (v: string) => void;
  handle: string;
  setHandle: (v: string) => void;
  handleStatus: HandleStatus;
}) {
  const { displayName, setDisplayName, handle, setHandle, handleStatus } = props;
  return (
    <>
      <h1 className="onb__heading">What should we call you?</h1>
      <p className="onb__blurb">
        Your handle is your @name. It's what people see on your posts and
        comments. Your name only shows on your profile page.
      </p>

      <div className="onb__field">
        <span className="onb__label" id="onb-handle-label">Handle</span>
        <div className="onb__handle" aria-labelledby="onb-handle-label">
          <span className="onb__handle-at">@</span>
          <input
            value={handle}
            onChange={(e) => setHandle(e.target.value.toLowerCase().replace(HANDLE_CHAR, ''))}
            maxLength={30}
            placeholder="handle"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
        </div>
        <HandleStatusLine status={handleStatus} />
        <p className="onb__hint">Lowercase letters, numbers, dashes, underscores. 3 to 30 characters.</p>
      </div>

      <div className="onb__field">
        <label htmlFor="onb-name">Name</label>
        <input
          id="onb-name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={80}
          placeholder="Your name"
          autoComplete="name"
        />
      </div>
    </>
  );
}

function HandleStatusLine({ status }: { status: HandleStatus }) {
  if (status.kind === 'idle') return <span className="onb__status onb__status--mute">&nbsp;</span>;
  if (status.kind === 'checking') return <span className="onb__status onb__status--mute">Checking…</span>;
  if (status.kind === 'ok') return <span className="onb__status onb__status--ok">✓ Yours.</span>;
  return <span className="onb__status onb__status--bad">✗ {status.reason}</span>;
}

function StepProfile(props: {
  avatarUrl: string;
  setAvatarUrl: (v: string) => void;
  bio: string;
  setBio: (v: string) => void;
  displayName: string;
  onPickAvatar: () => void;
  uploadMsg: string | null;
  country: string;
  setCountry: (v: string) => void;
  town: string;
  setTown: (v: string) => void;
  ig: string;
  setIg: (v: string) => void;
}) {
  const {
    avatarUrl, setAvatarUrl, bio, setBio, displayName, onPickAvatar, uploadMsg,
    country, setCountry, town, setTown, ig, setIg,
  } = props;
  return (
    <>
      <h1 className="onb__heading">Add a picture if you wish.</h1>
      <p className="onb__blurb">
        Add a photo of your choice, get creative, and a short bio. You can always
        add them later in your profile.
      </p>

      <div className="onb__upload">
        <span className="onb__label">Profile photo</span>
        <div className="onb__upload-row">
          <div className="onb__avatar-preview">
            {avatarUrl ? <img src={avatarUrl} alt="" /> : (
              <Avatar name={displayName || 'You'} size="lg" />
            )}
          </div>
          <div className="onb__upload-actions">
            <button type="button" className="btn btn--secondary btn--sm" onClick={onPickAvatar}>
              {avatarUrl ? 'Replace' : 'Upload'}
            </button>
            {avatarUrl && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setAvatarUrl('')}
              >
                Remove
              </button>
            )}
          </div>
        </div>
        {uploadMsg && <p className="onb__hint">{uploadMsg}</p>}
      </div>

      <div className="onb__field">
        <label htmlFor="onb-bio">Bio</label>
        <textarea
          id="onb-bio"
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          maxLength={500}
          rows={3}
          placeholder="The garment you can't stop re-cutting. Whatever you want us to know."
        />
      </div>

      <div className="onb__field">
        <span className="onb__label" id="onb-loc-label">Location</span>
        <div className="onb__loc-row" aria-labelledby="onb-loc-label">
          <CountrySelect value={country} onChange={setCountry} className="onb__select" compact />
          <input
            value={town}
            onChange={(e) => setTown(e.target.value)}
            maxLength={80}
            placeholder="City / area"
            aria-label="City or area (optional)"
          />
        </div>
        <p className="onb__hint">Country is required. City or area is optional.</p>
      </div>

      <div className="onb__field">
        <label htmlFor="onb-ig">Instagram</label>
        <div className="onb__handle">
          <span className="onb__handle-at">@</span>
          <input
            id="onb-ig"
            value={ig}
            onChange={(e) => setIg(e.target.value)}
            maxLength={60}
            placeholder="your handle"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
        </div>
      </div>
    </>
  );
}

function StepPassword(props: {
  pw1: string;
  setPw1: (v: string) => void;
  pw2: string;
  setPw2: (v: string) => void;
  pwError: string | null;
}) {
  const { pw1, setPw1, pw2, setPw2, pwError } = props;
  return (
    <>
      <h1 className="onb__heading">Set a password.</h1>

      <div className="onb__field">
        <label htmlFor="onb-pw1">Password</label>
        <input
          id="onb-pw1"
          type="password"
          autoComplete="new-password"
          minLength={8}
          placeholder="At least 8 characters"
          value={pw1}
          onChange={(e) => setPw1(e.target.value)}
        />
      </div>

      <div className="onb__field">
        <label htmlFor="onb-pw2">Confirm password</label>
        <input
          id="onb-pw2"
          type="password"
          autoComplete="new-password"
          minLength={8}
          placeholder="Type it again"
          value={pw2}
          onChange={(e) => setPw2(e.target.value)}
        />
      </div>

      {/* Live feedback so it's clear why "Next" is disabled until it's valid. */}
      {pw1.length > 0 && pw1.length < 8 && (
        <span className="onb__status onb__status--bad">✗ At least 8 characters.</span>
      )}
      {pw1.length >= 8 && pw2.length > 0 && pw1 !== pw2 && (
        <span className="onb__status onb__status--bad">✗ Passwords don't match.</span>
      )}
      {pw1.length >= 8 && pw1 === pw2 && (
        <span className="onb__status onb__status--ok">✓ Passwords match.</span>
      )}
      {pwError && pw1.length === 0 && (
        <span className="onb__status onb__status--bad">✗ {pwError}</span>
      )}
    </>
  );
}

function StepSummary(props: {
  displayName: string;
  handle: string;
  bio: string;
  avatarUrl: string;
  ig: string;
  country: string;
  town: string;
}) {
  const { displayName, handle, bio, avatarUrl, ig, country, town } = props;
  const cleanIg = ig.trim().replace(/^@/, '');
  // Flag + the free-text city only — the flag already says the country.
  const locText = town.trim();
  return (
    <>
      <h1 className="onb__heading">Here's how you'll appear.</h1>

      <div className="onb__summary">
        <div className="onb__summary-images">
          <div className="onb__summary-avatar">
            {avatarUrl ? <img src={avatarUrl} alt="" /> : null}
          </div>
        </div>
        <div className="onb__summary-row">
          <span className="onb__summary-label">Handle</span>
          <span className="onb__summary-value">@{handle.trim().toLowerCase() || '—'}</span>
        </div>
        <div className="onb__summary-row">
          <span className="onb__summary-label">Name</span>
          <span className="onb__summary-value">{displayName.trim() || '—'}</span>
        </div>
        <div className="onb__summary-row">
          <span className="onb__summary-label">Bio</span>
          <span className={`onb__summary-value${bio.trim() ? '' : ' onb__summary-value--muted'}`}>
            {bio.trim() || 'skipped'}
          </span>
        </div>
        <div className="onb__summary-row">
          <span className="onb__summary-label">Location</span>
          <span className={`onb__summary-value${country ? '' : ' onb__summary-value--muted'}`}>
            {country ? <><FlagIcon code={country} /> {locText}</> : '—'}
          </span>
        </div>
        <div className="onb__summary-row">
          <span className="onb__summary-label">Instagram</span>
          <span className={`onb__summary-value${cleanIg ? '' : ' onb__summary-value--muted'}`}>
            {cleanIg ? `@${cleanIg}` : 'skipped'}
          </span>
        </div>
      </div>
    </>
  );
}
